import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Stack, SwipeableDrawer, Typography } from "@mui/material";
import { useEffect, useRef, type ReactNode } from "react";

import { APP_UI } from "../ui/appUi";
import { useSheetDismiss } from "../hooks/useSheetDismiss";
import FastScrollThumb from "./FastScrollThumb";
import SheetScrollbarGuard from "./SheetScrollbarGuard";

const noopOpen = (): void => undefined;

export default function AppBottomSheet({
  open,
  onClose,
  title,
  subtitle,
  overline,
  leading,
  headerAction,
  children,
  maxHeight = "84dvh",
  zIndex = 1650,
  contentSpacing = 1.25,
  showClose = true,
  rearmAfterBack = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  overline?: string;
  leading?: ReactNode;
  headerAction?: ReactNode;
  children: ReactNode;
  maxHeight?: string;
  zIndex?: number;
  contentSpacing?: number;
  showClose?: boolean;
  rearmAfterBack?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { requestClose } = useSheetDismiss(open, onClose, rearmAfterBack);

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={noopOpen}
      onClose={requestClose}
      disableSwipeToOpen
      disableDiscovery
      disableBackdropTransition
      hysteresis={0.20}
      minFlingVelocity={220}
      transitionDuration={{ enter: 200, exit: 180 }}
      ModalProps={{ disableRestoreFocus: true, keepMounted: true }}
      slotProps={{
        paper: {
          sx: {
            width: "100%",
            maxWidth: 600,
            mx: "auto",
            maxHeight,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            borderRadius: APP_UI.sheetRadius,
            color: APP_UI.text,
            bgcolor: APP_UI.surface,
            backgroundImage: "none",
            border: `1px solid ${APP_UI.border}`,
          },
        },
      }}
      sx={{
        zIndex,
        "& .MuiBackdrop-root": { bgcolor: APP_UI.backdrop },
      }}
    >
      <Box
        sx={{
          minHeight: APP_UI.sheetHandleHeight,
          display: "grid",
          gridTemplateColumns: "44px 1fr 44px",
          alignItems: "center",
          px: 0.5,
          touchAction: "pan-y",
          cursor: "grab",
          flex: "0 0 auto",
          borderBottom: "1px solid rgba(255,255,255,0.035)",
        }}
      >
        <Box />
        <Box sx={{ width: 44, height: 4, borderRadius: 99, bgcolor: "rgba(255,255,255,0.30)", mx: "auto" }} />
        {showClose ? (
          <IconButton onClick={requestClose} aria-label="Schließen" sx={{ color: "#fff", width: 40, height: 40, justifySelf: "end", cursor: "pointer" }}>
            <CloseRoundedIcon />
          </IconButton>
        ) : <Box />}
      </Box>

      <Box sx={{ position: "relative", display: "flex", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
        <Box
          ref={contentRef}
          sx={{ p: 0, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", flex: "1 1 auto" }}
        >
          <Stack spacing={contentSpacing} sx={{ px: APP_UI.contentPx, pb: "calc(1.8rem + env(safe-area-inset-bottom))" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {leading}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {overline && (
                <Typography variant="caption" sx={{ color: APP_UI.accent, fontWeight: 850, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  {overline}
                </Typography>
              )}
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 900, lineHeight: 1.15 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" sx={{ color: APP_UI.textMuted, display: "block", mt: 0.15 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {headerAction}

          </Stack>
            {children}
          </Stack>
        </Box>
        <SheetScrollbarGuard>
          <FastScrollThumb targetRef={contentRef} contained minScrollablePx={1} zIndex={zIndex + 100} />
        </SheetScrollbarGuard>
      </Box>
    </SwipeableDrawer>
  );
}
